'use client';
import { targetScrapingParamInputFieldConfigMap } from '@decodo/utils';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useApi } from '@/hooks/useApi';
import { useTranslation } from '@/lib/i18n/i18n.client';

import { ResponseStatus } from '@/blocks/Scraper/components/ResponseStatus';
import Button from '@/components/atoms/Button';
import { Spinner } from '@/components/atoms/spinner';
import { findElClosestTheme } from '@/theme/themeProviderUtils';
import { filterObj, filterObjectByList } from '@/utils/objects';

import { ConfigForm } from './components/ConfigForm';
import { initFormValuesForTarget, isValidFormParam } from './helpers';
import { RequestResponse } from './RequestResponse';
import { useScraper } from './state/context';

export const ScraperMain = () => {
  const { target, setActiveTab, form } = useScraper();
  const { texts } = useTranslation();

  const [displayedResponse, setDisplayedResponse] = useState<any>(null);
  const [displayedError, setDisplayedError] = useState<string | null>(null);
  const [showingToast, setShowingToast] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  const { loading: isLoading, makeApiCall: scrape } = useApi({
    url: 'https://decodo.com/scraping/web/scraper-api',
    headers: { Origin: 'https://decodo.com/', 'x-target': target.name },
    method: 'POST',
  });

  // Omit `form` from deps — useForm returns a new object every render; including
  // it causes an infinite setState loop that freezes client navigation.
  const resetForm = useCallback(() => {
    form.setFormValues(initFormValuesForTarget(target));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  useEffect(() => {
    if (!target) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setDisplayedResponse(null);
    setDisplayedError(null);
    resetForm();
    setActiveTab('request');
  }, [target, setActiveTab, resetForm]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleSubmit = async (formState: Record<string, string | boolean>) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const params =
      targetScrapingParamInputFieldConfigMap[target.targetKey].filter(
        isValidFormParam
      );

    const payload = filterObjectByList(
      formState,
      params.map((param) => param.key)
    );

    // TODO: fix web not parsing
    const body = {
      target: target.name,
      ...filterObj(payload, (value) => !!value),
    };

    const response = await scrape({
      body,
      signal: abortController.signal,
    });

    if (abortController.signal.aborted) {
      return;
    }

    setActiveTab('response');
    setShowingToast(true);
    setDisplayedResponse(response?.data);

    // Abort controller's .abort behaves like an error, hense this explicit handling:
    const isAbortError =
      response?.error &&
      (String(response.error).includes('abort') ||
        String(response.error).includes('AbortError'));
    const errorMessage = response?.error ? String(response.error) : null;
    setDisplayedError(isAbortError ? null : errorMessage);

    setTimeout(() => {
      setShowingToast(false);
    }, 3000);
  };

  const ref = useRef<HTMLButtonElement>(null);
  const theme = ref.current ? findElClosestTheme(ref.current) : 'light';

  return (
    <div className='flex w-full flex-col gap-4'>
      <form
        className='flex flex-col gap-3 lg:flex-row h-full dark-scrollbar'
        onSubmit={form.handleSubmit(handleSubmit)}
        style={{
          // ensures scrollbars match the dark theme on safari
          colorScheme: 'dark',
        }}
      >
        <div className='lg:min-h-[620px] min-h-[520px] lg:w-1/2'>
          <ConfigForm form={form} />
        </div>
        <div className='lg:min-h-[620px] min-h-[520px] flex flex-col lg:w-1/2'>
          <RequestResponse
            form={form}
            response={displayedResponse}
            error={isLoading ? null : displayedError}
          />
          <div className='relative'>
            <div className='absolute bottom-12 z-10 h-10 right-3 left-3'>
              <ResponseStatus
                isVisible={showingToast}
                status={!!displayedError ? 'error' : 'success'}
              />
            </div>
            <Button
              isDecoro={true}
              type='submit'
              classNames={{
                root: 'absolute left-3 bottom-3 right-3 z-20 w-auto disabled:dark:text-base-dark-white disabled:text-gray-500',
              }}
              fullwidth
              ref={ref}
              disabled={isLoading}
              rightSection={
                isLoading && (
                  <Spinner
                    color={theme === 'light' ? 'gray-500' : 'base-black'}
                  />
                )
              }
            >
              {isLoading
                ? texts.scraper.sending_request
                : texts.scraper.send_request}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
